import axios from 'axios';
import { format } from 'date-fns';

// Define result types for better type safety
export interface ScanResult {
  title: string;
  url: string;
  snippet: string;
  query: string;
  page: number;
  date: string;
  contentType?: string;
  confidence?: number;
  domain?: string;
  discovered_date?: string;
}

/**
 * TypeScript implementation of the Python LeakScraper class
 * This follows the same logic as the Python implementation
 */
export class LeakScraper {
  private creatorName: string;
  private apiKey: string;
  private searchEngineId: string;
  private maxSearches: number;
  private apiCalls: number = 0;
  private uniqueUrls: Set<string> = new Set();
  private onProgressUpdate?: (progress: number, status: string) => void;

  constructor(
    creatorName: string, 
    apiKey: string, 
    searchEngineId: string, 
    maxSearches: number = 100,
    onProgressUpdate?: (progress: number, status: string) => void
  ) {
    this.creatorName = creatorName;
    this.apiKey = apiKey;
    this.searchEngineId = searchEngineId;
    this.maxSearches = maxSearches;
    this.onProgressUpdate = onProgressUpdate;
  }

  /**
   * Group keywords into batches with maximum 1 keyword per batch
   */
  private adaptiveBatchKeywords(keywords: string[], maxBatchSize: number = 1): string[][] {
    // Always create batches with max 1 keyword per batch
    const batches: string[][] = [];
    for (let i = 0; i < keywords.length; i += maxBatchSize) {
      batches.push(keywords.slice(i, i + maxBatchSize));
    }
    return batches;
  }

  /**
   * Build an optimized query from a batch of keywords
   */
  private buildQuery(keywordBatch: string[]): string {
    const queryParts: string[] = [];

    for (const keyword of keywordBatch) {
      // Check if the keyword already has quotes
      if (keyword.startsWith('"') && keyword.endsWith('"')) {
        queryParts.push(keyword);
      } else if (keyword.includes('site:')) {
        // Don't add quotes to site-specific searches
        queryParts.push(keyword);
      } else {
        // Add quotes for exact phrase matching
        queryParts.push(keyword);
      }
    }

    // Join with OR operator
    return queryParts.join(' OR ');
  }

  /**
   * Convert user timeframe to API date_restrict parameter
   */
  private getDateRestrict(timeframe: string): string {
    if (timeframe === 'today') {
      return 'd1';
    } else if (timeframe === 'lifetime') {
      return ''; // No date restriction for all time
    } else if (timeframe.includes('days')) {
      const days = parseInt(timeframe.split(' ')[1]);
      return `d${days}`;
    } else if (timeframe.includes('weeks')) {
      const weeks = parseInt(timeframe.split(' ')[1]);
      const days = weeks * 7;
      return `d${days}`;
    } else if (timeframe.includes('months')) {
      const months = parseInt(timeframe.split(' ')[1]);
      const days = months * 30;
      return `d${days}`;
    } else {
      // Default to last 30 days
      return 'd30';
    }
  }

  /**
   * Search for a batch of keywords with pagination
   */
  private async searchBatch(keywordBatch: string[], dateRestrict: string, maxPages: number = 10): Promise<ScanResult[]> {
    // Build combined query
    const query = this.buildQuery(keywordBatch);
    const encodedQuery = encodeURIComponent(query);

    console.log(`🔍 Searching batch: ${keywordBatch.slice(0, 3).join(' | ')}...`);
    if (keywordBatch.length > 3) {
      console.log(`   ...and ${keywordBatch.length - 3} more keywords`);
    }
    console.log(`   Query: ${query.length > 100 ? query.substring(0, 100) + '...' : query}`);

    // Store results
    const batchResults: ScanResult[] = [];

    // Search with pagination
    for (let page = 1; page <= maxPages; page++) {
      // Check if we've hit our search limit
      if (this.apiCalls >= this.maxSearches) {
        console.log(`⚠️ Reached search limit (${this.apiCalls}/${this.maxSearches})`);
        return batchResults;
      }

      // Calculate start index for pagination
      const startIndex = ((page - 1) * 10) + 1;

      // Build URL parameters
      const urlParams = [
        `q=${encodedQuery}`,
        `cx=${this.searchEngineId}`,
        `key=${this.apiKey}`,
        'num=10', // Always 10 (API limit)
        `start=${startIndex}`
      ];

      // Add date restriction only if specified (not empty for lifetime)
      if (dateRestrict) {
        urlParams.push(`dateRestrict=${dateRestrict}`);
      }

      // ALWAYS add exactTerms to ensure creator name is present
      urlParams.push(`exactTerms=${encodeURIComponent(this.creatorName)}`);

      // Build final URL
      const apiUrl = `https://www.googleapis.com/customsearch/v1?${urlParams.join('&')}`;

      try {
        console.log(`   📄 Page ${page}/${maxPages}...`);
        console.log(`   🔍 Query: ${decodeURIComponent(encodedQuery)}`);
        console.log(`   🌐 Full API URL: ${apiUrl}`);
        console.log(`   📋 URL Params:`, urlParams);
        const response = await axios.get(apiUrl);
        this.apiCalls++;
        const data = response.data;

        // Handle API errors
        if (data.error) {
          const errorMsg = data.error.message || 'Unknown error';
          console.log(`   ⚠️ API error: ${errorMsg}`);

          // Check for quota exceeded errors
          if (errorMsg.toLowerCase().includes('quota')) {
            console.log('❌ API quota exceeded! Stopping searches.');
            return batchResults;
          }

          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait longer if we hit an error
          continue;
        }

        if (data.items) {
          const resultCount = data.items.length;
          let newCount = 0;
          console.log(`\n   📋 Page ${page} Results: ${resultCount} items found`);
          console.log(`   📊 Search Info:`, data.searchInformation);
          if (data.queries) {
            console.log(`   🔍 Query Info:`, data.queries);
          }

          for (let i = 0; i < data.items.length; i++) {
            const item = data.items[i];
            const title = item.title || '';
            const link = item.link || '';
            const snippet = item.snippet || '';

            // Show all links in terminal
            const isNew = !this.uniqueUrls.has(link);
            const status = isNew ? '🆕' : '📎';
            console.log(`   ${status} ${i + 1}. ${title.substring(0, 50)}... - ${link}`);

            // Only add if it's a new URL
            if (isNew) {
              // Detect content type based on URL patterns
              const contentType = this.detectContentType(link);
              const domain = this.extractDomain(link);
              const confidence = this.calculateConfidence(title, snippet, keywordBatch);
              
              batchResults.push({
                title: title,
                url: link,
                snippet: snippet,
                query: JSON.stringify(keywordBatch),
                page: page,
                date: format(new Date(), 'yyyy-MM-dd'),
                contentType,
                confidence,
                domain,
                discovered_date: format(new Date(), 'yyyy-MM-dd')
              });
              this.uniqueUrls.add(link);
              newCount++;
            }
          }

          console.log(`\n   ✅ Found ${resultCount} results, ${newCount} new URLs`);

          // If we do not find new results on a new page
          if (resultCount === 0) {
            console.log('   ℹ️ No more results available');
            break;
          }
        } else {
          console.log('   ⚠️ No results found on this page');
          break; // No need to check more pages
        }

        // Respect API rate limits
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (e: any) {
        console.log(`   ⚠️ Error: ${e.message}`);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    return batchResults;
  }

  /**
   * Run a scan with user-provided keywords and timeframe
   */
  public async runScan(keywords: string[], timeframe: string, maxSearches?: number): Promise<ScanResult[]> {
    if (maxSearches !== undefined) {
      this.maxSearches = maxSearches;
    }

    console.log(`\n🚀 Starting leak scan for: ${this.creatorName}`);
    console.log(`📅 Timeframe: ${timeframe}`);
    console.log(`🔢 Search budget: ${this.maxSearches} API calls`);
    console.log(`🔍 Using ${keywords.length} keywords: ${keywords}`);

    // Reset API calls counter
    this.apiCalls = 0;

    // Convert timeframe to date_restrict parameter
    const dateRestrict = this.getDateRestrict(timeframe);
    console.log(`ℹ️ Converted timeframe to date parameter: ${dateRestrict || 'No date restriction (All Time)'}`);

    // Group keywords into appropriately sized batches
    const batches = this.adaptiveBatchKeywords(keywords);
    console.log(`ℹ️ Created ${batches.length} batches of keywords`);

    // Store all results
    const allResults: ScanResult[] = [];

    // Process each batch
    for (let batchNum = 0; batchNum < batches.length; batchNum++) {
      // Check if we've hit our search limit
      if (this.apiCalls >= this.maxSearches) {
        console.log(`⚠️ Reached search limit (${this.apiCalls}/${this.maxSearches})`);
        break;
      }

      console.log(`\n🔍 Processing batch ${batchNum + 1}/${batches.length}`);

      // Search this batch with pagination
      const batchResults = await this.searchBatch(batches[batchNum], dateRestrict);
      allResults.push(...batchResults);

      // Calculate and report progress
      const progress = Math.min(100, Math.round((this.apiCalls / this.maxSearches) * 100));
      const status = `Found ${allResults.length} unique URLs so far`;
      
      console.log(`Progress: ${this.apiCalls}/${this.maxSearches} API calls used (${progress}%)`);
      console.log(status);
      
      // Call progress callback if provided
      if (this.onProgressUpdate) {
        this.onProgressUpdate(progress, status);
      }
    }

    console.log(`\n✅ Scan complete!`);
    console.log(`🔢 API calls used: ${this.apiCalls}/${this.maxSearches}`);
    console.log(`🔗 Unique URLs found: ${allResults.length}`);

    // Print summary of top domains
    this.printDomainSummary(allResults);

    return allResults;
  }

  /**
   * Print a summary of the top domains found
   */
  private printDomainSummary(results: ScanResult[]): void {
    if (!results.length) {
      return;
    }

    // Extract domains from URLs
    const domains: string[] = [];
    for (const result of results) {
      const url = result.url;
      try {
        // Extract domain from URL
        const domain = this.extractDomain(url);
        domains.push(domain);
      } catch {
        // Skip if we can't parse the domain
      }
    }

    // Count domains
    const domainCounts: Record<string, number> = {};
    for (const domain of domains) {
      if (domain in domainCounts) {
        domainCounts[domain]++;
      } else {
        domainCounts[domain] = 1;
      }
    }

    // Sort by count
    const sortedDomains = Object.entries(domainCounts).sort((a, b) => b[1] - a[1]);

    // Print top domains
    console.log('\n📊 Top Domains with Content:');
    for (let i = 0; i < Math.min(sortedDomains.length, 10); i++) {
      const [domain, count] = sortedDomains[i];
      console.log(`   ${i + 1}. ${domain}: ${count} URLs`);
    }
  }
  
  /**
   * Detect content type based on URL patterns
   */
  private detectContentType(url: string): string {
    const videoDomains = ['youtube.com', 'vimeo.com', 'tiktok.com', 'twitch.tv', 'dailymotion.com'];
    const imageDomains = ['instagram.com', 'imgur.com', 'flickr.com', 'pinterest.com', 'unsplash.com'];
    const documentDomains = ['docs.google.com', 'dropbox.com', 'scribd.com', 'slideshare.net'];
    const socialDomains = ['facebook.com', 'twitter.com', 'linkedin.com', 'reddit.com'];
    
    const domain = this.extractDomain(url);
    
    if (videoDomains.some(d => domain.includes(d)) || url.includes('/video/') || url.includes('watch?v=')) {
      return 'video';
    } else if (imageDomains.some(d => domain.includes(d)) || url.match(/\.(jpg|jpeg|png|gif|webp)($|\?)/i)) {
      return 'image';
    } else if (documentDomains.some(d => domain.includes(d)) || url.match(/\.(pdf|doc|docx|ppt|pptx|xls|xlsx)($|\?)/i)) {
      return 'document';
    } else if (socialDomains.some(d => domain.includes(d))) {
      return 'social';
    } else {
      return 'website';
    }
  }
  
  /**
   * Extract domain from URL
   */
  private extractDomain(url: string): string {
    try {
      return url.split('//')[1].split('/')[0];
    } catch {
      return '';
    }
  }
  
  /**
   * Calculate confidence score based on content relevance
   */
  private calculateConfidence(title: string, snippet: string, keywords: string[]): number {
    let score = 0.5; // Base score
    
    // Check if title contains creator name
    if (title.toLowerCase().includes(this.creatorName.toLowerCase())) {
      score += 0.2;
    }
    
    // Check for keyword matches
    const combinedText = (title + ' ' + snippet).toLowerCase();
    let keywordMatches = 0;
    
    for (const keyword of keywords) {
      const keywordText = keyword.toLowerCase().replace(this.creatorName.toLowerCase(), '').trim();
      if (keywordText && combinedText.includes(keywordText)) {
        keywordMatches++;
      }
    }
    
    // Add score based on keyword matches
    score += Math.min(0.3, keywordMatches * 0.1);
    
    // Limit score to 0-1 range
    return Math.min(1, Math.max(0, score));
  }
}
